/**
 * Datos de las herramientas interactivas.
 * - QUIZ: recomendador de seguros (puntuación por producto)
 * - COMPARE: comparador educativo de coberturas
 *
 * Nota importante: el recomendador es orientativo. Nunca sustituye
 * una evaluación con un asesor licenciado, y así se comunica en la UI.
 */

/* ------------------------------------------------------------------ */
/* Recomendador                                                        */
/* ------------------------------------------------------------------ */

export const QUIZ = [
  {
    id: "objetivo",
    question: {
      es: "¿Qué es lo que más te preocupa hoy?",
      en: "What worries you most today?",
    },
    help: {
      es: "Elige lo primero que te vino a la mente. No hay respuesta incorrecta.",
      en: "Pick the first thing that came to mind. There is no wrong answer.",
    },
    options: [
      {
        id: "familia",
        label: { es: "Que a mi familia le falte ingreso", en: "My family losing income" },
        desc: {
          es: "Si algo me pasa, quiero que sigan igual.",
          en: "If something happens to me, I want them to be fine.",
        },
        weights: { "8-seguros-en-1": 3, cancer: 1, "gastos-finales": 2, retiro: 0 },
      },
      {
        id: "enfermedad",
        label: { es: "Un diagnóstico grave", en: "A serious diagnosis" },
        desc: {
          es: "Me preocupa el costo de un tratamiento largo.",
          en: "I worry about the cost of long treatment.",
        },
        weights: { "8-seguros-en-1": 3, cancer: 4, "gastos-finales": 0, retiro: 0 },
      },
      {
        id: "funeral",
        label: { es: "Dejar gastos a mis hijos", en: "Leaving costs to my children" },
        desc: {
          es: "Quiero el funeral resuelto por adelantado.",
          en: "I want the funeral settled in advance.",
        },
        weights: { "8-seguros-en-1": 1, cancer: 0, "gastos-finales": 4, retiro: 0 },
      },
      {
        id: "retiro",
        label: { es: "No tener suficiente al retirarme", en: "Not having enough at retirement" },
        desc: {
          es: "Quiero construir un ingreso para el futuro.",
          en: "I want to build income for the future.",
        },
        weights: { "8-seguros-en-1": 0, cancer: 0, "gastos-finales": 1, retiro: 4 },
      },
    ],
  },
  {
    id: "dependientes",
    question: {
      es: "¿Quién depende económicamente de ti?",
      en: "Who depends on you financially?",
    },
    options: [
      {
        id: "nadie",
        label: { es: "Nadie por ahora", en: "Nobody right now" },
        weights: { "8-seguros-en-1": 1, cancer: 2, "gastos-finales": 1, retiro: 3 },
      },
      {
        id: "pareja",
        label: { es: "Mi pareja", en: "My partner" },
        weights: { "8-seguros-en-1": 3, cancer: 2, "gastos-finales": 2, retiro: 2 },
      },
      {
        id: "hijos",
        label: { es: "Hijos menores", en: "Minor children" },
        weights: { "8-seguros-en-1": 4, cancer: 3, "gastos-finales": 1, retiro: 1 },
      },
      {
        id: "padres",
        label: { es: "Padres o familiares mayores", en: "Parents or older relatives" },
        weights: { "8-seguros-en-1": 2, cancer: 2, "gastos-finales": 3, retiro: 1 },
      },
    ],
  },
  {
    id: "edad",
    question: { es: "¿En qué rango de edad estás?", en: "What is your age range?" },
    options: [
      {
        id: "18-34",
        label: { es: "18 a 34", en: "18 to 34" },
        weights: { "8-seguros-en-1": 3, cancer: 2, "gastos-finales": 0, retiro: 3 },
      },
      {
        id: "35-49",
        label: { es: "35 a 49", en: "35 to 49" },
        weights: { "8-seguros-en-1": 4, cancer: 3, "gastos-finales": 1, retiro: 3 },
      },
      {
        id: "50-64",
        label: { es: "50 a 64", en: "50 to 64" },
        weights: { "8-seguros-en-1": 3, cancer: 3, "gastos-finales": 3, retiro: 2 },
      },
      {
        id: "65",
        label: { es: "65 o más", en: "65 or older" },
        weights: { "8-seguros-en-1": 1, cancer: 2, "gastos-finales": 4, retiro: 1 },
      },
    ],
  },
  {
    id: "salud",
    question: {
      es: "¿Tienes plan médico actualmente?",
      en: "Do you currently have a health plan?",
    },
    help: {
      es: "Esto cambia la recomendación: los planes suplementarios complementan, no sustituyen.",
      en: "This changes the recommendation: supplemental plans complement, they do not replace.",
    },
    options: [
      {
        id: "si",
        label: { es: "Sí, tengo plan médico", en: "Yes, I have a health plan" },
        weights: { "8-seguros-en-1": 3, cancer: 3, "gastos-finales": 2, retiro: 2 },
      },
      {
        id: "no",
        label: { es: "No tengo", en: "I do not have one" },
        desc: {
          es: "Te orientaremos primero sobre cubierta médica.",
          en: "We will guide you on medical coverage first.",
        },
        weights: { "8-seguros-en-1": 4, cancer: 2, "gastos-finales": 2, retiro: 0 },
      },
      {
        id: "nosé",
        label: { es: "No estoy seguro de qué cubre", en: "I am not sure what it covers" },
        weights: { "8-seguros-en-1": 3, cancer: 3, "gastos-finales": 1, retiro: 1 },
      },
    ],
  },
  {
    id: "presupuesto",
    question: {
      es: "¿Cuánto podrías destinar mensualmente sin que te apriete?",
      en: "How much could you set aside monthly without strain?",
    },
    help: {
      es: "Preferimos una prima que puedas sostener por años antes que una póliza que caduque.",
      en: "We prefer a premium you can sustain for years over a policy that lapses.",
    },
    options: [
      {
        id: "bajo",
        label: { es: "Menos de $50", en: "Under $50" },
        weights: { "8-seguros-en-1": 2, cancer: 3, "gastos-finales": 3, retiro: 1 },
      },
      {
        id: "medio",
        label: { es: "Entre $50 y $150", en: "Between $50 and $150" },
        weights: { "8-seguros-en-1": 4, cancer: 3, "gastos-finales": 3, retiro: 3 },
      },
      {
        id: "alto",
        label: { es: "Más de $150", en: "More than $150" },
        weights: { "8-seguros-en-1": 4, cancer: 2, "gastos-finales": 2, retiro: 4 },
      },
      {
        id: "nose",
        label: { es: "Prefiero que me orienten", en: "I would rather be guided" },
        weights: { "8-seguros-en-1": 3, cancer: 2, "gastos-finales": 2, retiro: 2 },
      },
    ],
  },
];

/** Calcula el porcentaje de afinidad por producto a partir de las respuestas. */
export function scoreQuiz(answers) {
  const totals = {};
  const maxima = {};

  QUIZ.forEach((step) => {
    const chosen = step.options.find((o) => o.id === answers[step.id]);
    const best = step.options.reduce((acc, option) => {
      Object.entries(option.weights).forEach(([key, value]) => {
        acc[key] = Math.max(acc[key] ?? 0, value);
      });
      return acc;
    }, {});

    Object.entries(best).forEach(([key, value]) => {
      maxima[key] = (maxima[key] ?? 0) + value;
    });

    if (!chosen) return;
    Object.entries(chosen.weights).forEach(([key, value]) => {
      totals[key] = (totals[key] ?? 0) + value;
    });
  });

  return Object.entries(totals)
    .map(([id, value]) => ({
      id,
      match: Math.round((value / (maxima[id] || 1)) * 100),
    }))
    .sort((a, b) => b.match - a.match);
}

/* ------------------------------------------------------------------ */
/* Comparador educativo                                                */
/* ------------------------------------------------------------------ */

export const COMPARE_COLUMNS = [
  { id: "8-seguros-en-1", name: { es: "8 Seguros en 1", en: "8 Insurances in 1" } },
  { id: "cancer", name: { es: "Cáncer", en: "Cancer" } },
  { id: "gastos-finales", name: { es: "Gastos Finales", en: "Final Expenses" } },
  { id: "retiro", name: { es: "Retiro y Ahorro", en: "Retirement & Savings" } },
];

/** yes = cubre · partial = parcial o según plan · no = no aplica */
export const COMPARE_ROWS = [
  {
    need: {
      es: "Paga en efectivo directamente a ti",
      en: "Pays cash directly to you",
    },
    values: { "8-seguros-en-1": "yes", cancer: "yes", "gastos-finales": "no", retiro: "partial" },
  },
  {
    need: {
      es: "Beneficio por diagnóstico de cáncer",
      en: "Cancer diagnosis benefit",
    },
    values: { "8-seguros-en-1": "yes", cancer: "yes", "gastos-finales": "no", retiro: "no" },
  },
  {
    need: { es: "Cubre accidentes y fracturas", en: "Covers accidents and fractures" },
    values: { "8-seguros-en-1": "yes", cancer: "no", "gastos-finales": "no", retiro: "no" },
  },
  {
    need: { es: "Pago por día de hospitalización", en: "Daily hospitalization benefit" },
    values: { "8-seguros-en-1": "yes", cancer: "partial", "gastos-finales": "no", retiro: "no" },
  },
  {
    need: {
      es: "Ingreso mensual por incapacidad",
      en: "Monthly disability income",
    },
    values: { "8-seguros-en-1": "yes", cancer: "no", "gastos-finales": "no", retiro: "no" },
  },
  {
    need: { es: "Cubre gastos funerales", en: "Covers funeral expenses" },
    values: { "8-seguros-en-1": "partial", cancer: "no", "gastos-finales": "yes", retiro: "no" },
  },
  {
    need: {
      es: "Deja dinero a un beneficiario",
      en: "Leaves money to a beneficiary",
    },
    values: { "8-seguros-en-1": "partial", cancer: "no", "gastos-finales": "yes", retiro: "yes" },
  },
  {
    need: { es: "Acumula valor en el tiempo", en: "Accumulates value over time" },
    values: { "8-seguros-en-1": "no", cancer: "no", "gastos-finales": "partial", retiro: "yes" },
  },
  {
    need: {
      es: "Genera ingreso durante el retiro",
      en: "Generates retirement income",
    },
    values: { "8-seguros-en-1": "no", cancer: "no", "gastos-finales": "no", retiro: "yes" },
  },
  {
    need: {
      es: "Emisión sin examen médico",
      en: "Issued without medical exam",
    },
    values: { "8-seguros-en-1": "partial", cancer: "partial", "gastos-finales": "yes", retiro: "partial" },
  },
];
